/**
 * pendingFriendInvite — durable hand-off of a friend-invite token across the
 * signup → (email verification) → sign-in round trip (B-WP11c).
 *
 * Mirrors pendingGroupJoin exactly (same persistence + degradation rationale): on
 * prod REQUIRE_EMAIL_VERIFICATION is ON, so /auth/signup returns no session and
 * the verification link carries no token. To auto-accept the invite once the
 * session finally lands, the token must survive OUTSIDE the URL — localStorage
 * with a bounded 24h TTL, cleared the moment it is consumed.
 *
 * Security: the token is already a bearer credential in the /app/friend-invite/
 * URL (which sets no-referrer). Persisting it in localStorage rather than
 * threading it through the /sign-up + verification URLs keeps it off the signup
 * page's address bar. Every access is guarded; on failure the store no-ops and
 * the flow degrades to the URL returnTo path (the landing page, where the user
 * can accept manually).
 */

const KEY = "proof.pending-friend-invite";
const TTL_MS = 24 * 60 * 60 * 1000;

interface Stored {
  token: string;
  expiresAt: number;
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Remember a friend-invite token so it survives the signup/verification round trip. */
export function storePendingFriendInvite(token: string): void {
  if (!token) return;
  const s = storage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify({ token, expiresAt: Date.now() + TTL_MS } satisfies Stored));
  } catch (err) {
    console.debug({ event: "pending_friend_invite_store_failed", err });
  }
}

/** Read the pending token if present and unexpired; clears (and returns null) otherwise. */
export function readPendingFriendInvite(): string | null {
  const s = storage();
  if (!s) return null;
  let raw: string | null = null;
  try {
    raw = s.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (typeof parsed.token !== "string" || typeof parsed.expiresAt !== "number") {
      clearPendingFriendInvite();
      return null;
    }
    if (Date.now() > parsed.expiresAt) {
      clearPendingFriendInvite();
      return null;
    }
    return parsed.token;
  } catch {
    clearPendingFriendInvite();
    return null;
  }
}

/** Forget any pending friend-invite token. */
export function clearPendingFriendInvite(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch (err) {
    console.debug({ event: "pending_friend_invite_clear_failed", err });
  }
}

/**
 * Post-auth destination resolver for a pending friend invite. If one is present,
 * consume it and route to the friend-invite landing with the auto-accept flag;
 * otherwise return the caller's already-sanitised fallback. Kept separate from
 * pendingGroupJoin's resolver — a caller with BOTH pending (edge case) resolves
 * the group join first (its resolver runs first in the sign-in flow), then this.
 */
export function resolvePendingFriendInviteRedirect(fallback: string): string {
  const token = readPendingFriendInvite();
  if (token) {
    clearPendingFriendInvite();
    return `/app/friend-invite/${encodeURIComponent(token)}?viaSignup=1`;
  }
  return fallback;
}
